import React from 'react'

import {
  getRatingBarColor,
  getSentimentBarColor,
} from 'features/charts-v2/constants'
import { chartConfig } from 'features/charts-v2/services/chart-config-service'
import { type MetricType } from 'features/charts-v2/types'
import {
  SegmentClickPayload,
  TransformedDataPoint,
} from 'features/charts-v2/types/chart-strategy'
import {
  formatMetricValue,
  getMetricLabel,
} from 'features/charts-v2/utils/metric-format-utils'
import { ALIAS_FOR_NULL_VALUES } from 'src/constants/filter-values'

// Treemap node data structure
interface TreemapNodeData extends TransformedDataPoint {
  name?: string
  size?: number
  x?: number
  y?: number
}

// Root structure from Recharts
interface TreemapRoot {
  children?: TreemapNodeData[]
}

/**
 * Custom Treemap Cell Component
 */
export interface TreemapCellProps {
  root?: TreemapRoot
  depth?: number
  x?: number
  y?: number
  width?: number
  height?: number
  index?: number
  payload?: TreemapNodeData
  colors?: string[]
  rank?: number
  selectedMetrics?: MetricType[]
  onSegmentClick?: (
    segmentData: SegmentClickPayload,
    event: React.MouseEvent,
  ) => void
  metricTotals: Record<string, number>
  /**
   * Stable label→color map built from the unfiltered reference dataset so that
   * hiding a breakdown does not re-index the positional palette.
   * When absent the cell falls back to the positional treemap palette.
   */
  breakdownColorMap?: Map<string, string>
}

export const CustomTreemapCell: React.FC<TreemapCellProps> = (props) => {
  const {
    x = 0,
    y = 0,
    width = 0,
    height = 0,
    payload,
    index = 0,
    root,
    selectedMetrics = [],
    onSegmentClick,
    breakdownColorMap,
  } = props

  // For Recharts Treemap, the individual cell data is in the root.children array
  let data: TreemapNodeData | null = null

  if (root && root.children && Array.isArray(root.children)) {
    // Try to find the data item that matches this cell's position/size
    data =
      root.children.find((child: TreemapNodeData, i: number) => {
        return i === index || (child.x === x && child.y === y)
      }) ?? null

    // If we can't find by position, use index as fallback
    if (!data && root.children[index]) {
      data = root.children[index]
    }
  }

  // Fallback to payload or props
  data = data || payload || (props as unknown as TreemapNodeData)

  if (!data || !width || !height || width < 30 || height < 30) {
    return null
  }

  // Determine fill color based on secondary metric (first metric determines size)
  let color: string
  const colorMetric = selectedMetrics[1]
  const colorMetricValue = colorMetric
    ? (data[`metric_${colorMetric}`] as number | undefined)
    : undefined

  if (colorMetric === 'sentiment' && colorMetricValue != null) {
    color = getSentimentBarColor(colorMetricValue)
  } else if (colorMetric === 'rating' && colorMetricValue != null) {
    color = getRatingBarColor(colorMetricValue)
  } else {
    color =
      breakdownColorMap?.get(data.name ?? ALIAS_FOR_NULL_VALUES) ??
      chartConfig.getColor('treemap', index)
  }

  const textColor = '#ffffff'
  const padding = 10
  const contentWidth = width - padding * 2
  const labelValueGap = 1

  // Cap font size at 20px max, scale appropriately
  const fontSize = Math.min(20, Math.max(12, width / 15))
  const smallFontSize = Math.min(14, Math.max(10, width / 18))
  const titleLineHeight = fontSize * 1.3
  const bodyLineHeight = smallFontSize * 1.4

  const { metricTotals } = props

  // Build metric annotations
  const metricAnnotations: Array<{ label: string; value: string }> = []
  selectedMetrics.forEach((metricType) => {
    const metricValue = data![`metric_${metricType}`] as number | undefined
    if (metricValue != null) {
      metricAnnotations.push({
        label: getMetricLabel(metricType),
        value: formatMetricValue(metricValue, metricType),
      })

      // Add percentage line for record_count and sentence_count
      if (
        (metricType === 'record_count' || metricType === 'sentence_count') &&
        metricTotals[metricType] != null &&
        metricTotals[metricType] > 0
      ) {
        const percentage = (metricValue / metricTotals[metricType]!) * 100
        metricAnnotations.push({
          label: `% ${metricType === 'record_count' ? 'Records' : 'Sentences'}`,
          value: `${percentage.toFixed(2)}%`,
        })
      }
    }
  })

  // Also check for record_count and sentence_count that might not be in selectedMetrics
  const volumeMetrics = ['record_count', 'sentence_count'] as const
  volumeMetrics.forEach((metricType) => {
    // Only add if not already in selectedMetrics (to avoid duplicates)
    if (!selectedMetrics.includes(metricType)) {
      const metricValue = data![`metric_${metricType}`] as number | undefined
      if (
        metricValue != null &&
        metricTotals[metricType] != null &&
        metricTotals[metricType] > 0
      ) {
        const percentage = (metricValue / metricTotals[metricType]!) * 100
        metricAnnotations.push({
          label: `% ${metricType === 'record_count' ? 'Records' : 'Sentences'}`,
          value: `${percentage.toFixed(2)}%`,
        })
      }
    }
  })

  // Calculate approximate text widths for centering the annotation block
  const estimateTextWidth = (text: string, fontSize: number): number => {
    return text.length * fontSize * 0.55
  }

  const maxLabelWidth = Math.max(
    ...metricAnnotations.map((a) =>
      estimateTextWidth(a.label + ':', smallFontSize),
    ),
    0,
  )
  const maxValueWidth = Math.max(
    ...metricAnnotations.map((a) => estimateTextWidth(a.value, smallFontSize)),
    0,
  )
  const totalAnnotationWidth = maxLabelWidth + labelValueGap * 2 + maxValueWidth

  // Helper to wrap text into lines
  const wrapText = (
    text: string,
    maxWidth: number,
    fontSize: number,
  ): string[] => {
    const words = text.split(' ')
    const lines: string[] = []
    let currentLine = ''

    // Approximate character width (rough estimate for proportional fonts)
    const charWidth = fontSize * 0.55
    const maxChars = Math.floor(maxWidth / charWidth)

    words.forEach((word) => {
      const testLine = currentLine ? `${currentLine} ${word}` : word
      if (testLine.length <= maxChars) {
        currentLine = testLine
      } else {
        if (currentLine) {
          lines.push(currentLine)
        }
        currentLine = word
      }
    })

    if (currentLine) {
      lines.push(currentLine)
    }

    return lines
  }

  // Wrap title text
  const titleLines = wrapText(
    data.name || ALIAS_FOR_NULL_VALUES,
    contentWidth,
    fontSize,
  )
  const totalTitleHeight = titleLines.length * titleLineHeight

  // Calculate how many annotation lines we can fit
  const spaceBetween = 8
  const availableHeight = height - padding * 2 - totalTitleHeight - spaceBetween
  const maxAnnotationLines = Math.floor(availableHeight / bodyLineHeight)

  // Determine visible annotations based on available space. The block is
  // centered at totalAnnotationWidth, so if that exceeds the cell's content
  // width the labels (left-aligned) and values (right-aligned) spill out of the
  // cell into its neighbors. Require a horizontal fit too, and otherwise show the
  // title alone rather than letting the metrics bleed across cell borders.
  const annotationsFitWidth = totalAnnotationWidth <= contentWidth
  const visibleAnnotations = annotationsFitWidth
    ? metricAnnotations.slice(0, Math.max(0, maxAnnotationLines))
    : []

  // Calculate total content height for vertical centering
  const totalAnnotationsHeight = visibleAnnotations.length * bodyLineHeight
  const totalContentHeight =
    totalTitleHeight +
    (visibleAnnotations.length > 0 ? spaceBetween : 0) +
    totalAnnotationsHeight
  const startY = y + (height - totalContentHeight) / 2

  // Clip all cell text to the cell rect so nothing (a long title word, a wide
  // value) can bleed past the cell border into a neighbor.
  const clipId = `treemap-clip-${index}-${Math.round(x)}-${Math.round(y)}`

  return (
    <g data-treemap-name={data.name}>
      <defs>
        <clipPath id={clipId}>
          <rect x={x} y={y} width={width} height={height} />
        </clipPath>
      </defs>
      <rect
        x={x}
        y={y}
        width={width}
        height={height}
        style={{
          fill: color,
          stroke: '#fff',
          strokeWidth: 2,
          cursor: onSegmentClick ? 'pointer' : 'default',
        }}
        onClick={(event: React.MouseEvent) => {
          if (onSegmentClick && data) {
            onSegmentClick(
              {
                primaryBreakdown: data.name || ALIAS_FOR_NULL_VALUES,
                value: (data.size as number) || 0,
                payload: data as unknown as Record<
                  string,
                  string | number | boolean | null
                >,
              },
              event,
            )
          }
        }}
      />
      {width > 60 && height > 40 && (
        <g clipPath={`url(#${clipId})`}>
          {/* Title - wrapped text with font-weight 500 */}
          {titleLines.map((line, idx) => (
            <text
              key={`title-${idx}`}
              x={x + width / 2}
              y={startY + titleLineHeight * (idx + 0.75)}
              textAnchor="middle"
              fill={textColor}
              style={{
                fontFamily: 'Poppins, sans-serif',
                fontSize,
                fontWeight: 300,
              }}
            >
              {line}
            </text>
          ))}

          {/* Metric annotations with font-weight 500 */}
          {visibleAnnotations.map((annotation, idx) => {
            const yPos =
              startY +
              totalTitleHeight +
              spaceBetween +
              bodyLineHeight * (idx + 0.75)
            // Center the entire annotation block within the cell
            const blockCenterX = x + width / 2
            // Calculate positions: left-align labels, right-align values
            const labelX = blockCenterX - totalAnnotationWidth / 2
            const valueX = blockCenterX + totalAnnotationWidth / 2
            return (
              <g key={idx}>
                {/* Label - left-aligned */}
                <text
                  x={labelX}
                  y={yPos}
                  textAnchor="start"
                  fill={textColor}
                  style={{
                    fontFamily: 'Poppins, sans-serif',
                    fontSize: smallFontSize,
                    fontWeight: 200,
                  }}
                >
                  {annotation.label}:
                </text>
                {/* Value - right-aligned */}
                <text
                  x={valueX}
                  y={yPos}
                  textAnchor="end"
                  fill={textColor}
                  style={{
                    fontFamily: 'Poppins, sans-serif',
                    fontSize: smallFontSize,
                    fontWeight: 200,
                  }}
                >
                  {annotation.value}
                </text>
              </g>
            )
          })}
        </g>
      )}
    </g>
  )
}
